/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorSaga" />
import * as React from "react";
import { Channel, channel, effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import * as AuthorConstants from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/AuthorConstants";
import { AuthorActions, AuthorActionTypes } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorActions";
import { IColumnAndSortOrder, ITestCaseItem, ITestCaseFilterValues, GridOptions } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorContracts";
import { filterTestCases, fetchFilteredSuiteTestCaseCounts } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorFilterHelper";
import { announce } from "VSS/Core/Util/Accessibility";
import { TestCaseCountText, TestCaseWorkItemType, ZeroTestCaseText } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Resources";
import {
    getColumnAndSortOrder,
    getTestCaseItemsFromState,
    getTestCasesCount,
    getTestCasesFromState,
    getTestCasesMapFromState,
    getWITColumnOptionFields,
    getWITColumnOptionFieldsArray,
    isAnyAuthorFilterValueSet
} from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorSelector";
import { TestCaseCSVExporterUtility } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/TestCaseCSVExporterUtility";
import { getTestCaseItemsComparator } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/TestCaseItemsSortingUtility";
import { OtherColumnOptions, isCustomField } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ColumnOptionsCommonUtils";
import { getAllColumnValues, getAllPossibleFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/DefineColumnOptionUtils";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { SuitesActions, SuitesActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { getTestSuiteById } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesSelector";
import { TestHubRefreshActions, TestHubRefreshActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import {
    getDefineColumnOptionFields,
    getIdentityDisplayString,
    getInitiallySelectedSuiteId,
    getPlanId,
    getTestCasePageSize,
    isAdvancedLicenseEnabled
} from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { getDataProviderContent, showErrorToast, errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import { testHubRefreshLocalContextData } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import {
    Configuration,
    ExcludeFlags,
    SuiteEntryTypes,
    SuiteEntryUpdateParams,
    SuiteTestCaseCreateUpdateParameters,
    ExportTestCaseParams,
    TestCase,
    TestPlansHubRefreshData,
    WorkItem,
    CloneTestCaseOperationInformation
} from "TestPlanCommon/Contracts/TestPlan";
import { TestArtifactWithContinuationToken } from "TestPlanCommon/Contracts/TestPlanExtended";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import {
    ContentViewObserverActions,
    ContentViewObserverActionTypes,
    getCurrentFilterState,
    getFilterDetails,
    isFilterActive,
    isFilterVisible
} from "VSS/Redux/Modules/ContentViewObserver";
import { IFilterDetails } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SortOrder } from "VSSUI/Table";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { IVssLocationService } from "VSS/Platform/Location";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { CloneOperationState } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { WorkItemTypeCategory } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { checkIfIsIE } from "Test/TestPlansHubRefresh/TestHub/Util";

interface ILoadTestCasesPayload {
    planId: number;
    suiteId: number;
    testCaseIds: number[];
}

interface IUpdateTestCasePayload {
    testCaseId: number;
    testCaseTitle: string;
    testCaseState: string;
    testCaseAssignedTo: string;
    columnOptions: { [id: string]: any };
}

export function* AuthorSaga(): SagaIterator {
    yield effects.takeLatest(AuthorActionTypes.initializeAuthorPivot, _initializeAuthorPivot);
    yield effects.debounce(100, ContentViewObserverActionTypes.FilterStateChanged, _filterTestCasesInState);
    yield effects.debounce(200, ContentViewObserverActionTypes.FilterStateChanged, _fetchSuiteTestCasesCount);
    yield effects.takeLatest(AuthorActionTypes.filterTestCasesInState, _filterTestCasesInState);
    yield effects.takeLatest(AuthorActionTypes.fetchSuiteTestCasesCount, _fetchSuiteTestCasesCount);
    yield effects.takeLatest(SuitesActionsTypes.moveTestCaseToSuiteSucceeded, _fetchSuiteTestCasesCount);
    yield effects.takeLatest(ContentViewObserverActionTypes.FilterToggled, _filterToggled);
    yield effects.takeLatest(AuthorActionTypes.loadInitialTestCases, _loadInitialTestCases);
    yield effects.takeLatest(SuitesActionsTypes.changeSelectedSuite, _changeSelectedSuite);
    yield effects.takeLatest(TestHubRefreshActionsTypes.editColumnOptionFieldsClicked, _editColumnOptionFieldsClicked);
    yield effects.takeLatest(AuthorActionTypes.loadTestCasesForSelectedSuite, _loadTestCasesForSelectedSuite);
    yield effects.takeLatest(AuthorActionTypes.addExistingTestCases, _addExistingTestCases);
    yield effects.takeLatest(TestHubRefreshActionsTypes.showSuiteLevelGrid, _showSuiteLevelGrid);
    yield effects.takeLatest(AuthorActionTypes.addOrEditTestCasesUsingGrid, _addOrEditTestCasesUsingGrid);
    yield effects.takeLatest(TestHubRefreshActionsTypes.closeGrid, _closeGrid);
    yield effects.takeLatest(AuthorActionTypes.assignConfigurationToTestCases, _assignConfigurationToTestCases);
    yield effects.takeLatest(AuthorActionTypes.openNewTestCaseWitForm, _openNewTestCaseWitForm);
    yield effects.takeLatest(AuthorActionTypes.openExistingTestCaseWitForm, _openExistingTestCaseWitForm);
    yield effects.takeLatest(AuthorActionTypes.getTestCasesFromServer, _getTestCasesFromServer);
    yield effects.takeLatest(AuthorActionTypes.removeTestCasesFromSuite, _removeTestCasesFromSuite);
    yield effects.takeLatest(AuthorActionTypes.reorderTestCases, _reorderTestCases);
    yield effects.takeLatest(AuthorActionTypes.bulkEditTestCases, _bulkEditTestCases);
    yield effects.takeLatest(AuthorActionTypes.saveColumnWidths, _saveColumnWidths);
    yield effects.takeLatest(AuthorActionTypes.fetchColumnWidths, _fetchColumnWidths);
    yield effects.takeLatest(SuitesActionsTypes.exportTestCasesToCSV, _exportTestCasesToCSV);
    yield effects.takeLatest(SuitesActionsTypes.exportTestCasesToExcel, _exportTestCasesToExcel);
    yield effects.takeLatest(AuthorActionTypes.getTestCasesCloneOpState, _getTestCasesCloneOpState);
    yield effects.takeLatest(AuthorActionTypes.openSuiteInNewWindow, _openSuiteInNewWindow);
}   

/**
 * Initialize actions for author pivot goes here
 */
export function* initializeAuthorPivot(
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;
    if (!pageData || !pageData.project) {
        throw new Error("Project missing");
    }

    const projectId = pageData.project.id;

    let initialFilterDetails: IFilterDetails = {
        projectId,
        filterState: {},
        filterVisible: false,
        filterKey: "testPlansHubFilterKey"
    };

    // get the current filter state from store and re-store the values that were set in execute tab in author tab
    const currentFilterDetails: IFilterDetails = (yield effects.select(getFilterDetails)) as any;
    if (currentFilterDetails) {
        // restore the entire filter details
        initialFilterDetails = currentFilterDetails;

        // restore the text filter value from execute tab
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY]) {
            const executeTextFilterValue: string = currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY]!.value;
            if (executeTextFilterValue && executeTextFilterValue.trim().length > 0) {
                initialFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY] =
                    currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY];
            }
        }

        // restore the state filter values from execute tab
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY]) {
            initialFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY] =
                currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY];
        }

        // restore the assignedto filter values from execute tab
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY]) {
            initialFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY] =
                currentFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY];
        }
    }

    yield effects.put(ContentViewObserverActions.initializeFilterState(initialFilterDetails));
    yield effects.put(SuitesActions.updateLoadingStatus(true));
}

/**
 * Runs to update filtered test cases wrt filter state
 */

export function* filterTestCasesInState(): SagaIterator {
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    let defineColumnOptionFields: string[] | undefined = (yield effects.select(getDefineColumnOptionFields)) as any;
    let fetchTags: boolean = false;
    if (defineColumnOptionFields) {
        for (const field of defineColumnOptionFields) {
            if (field === TestHubRefreshConstants.WorkItemField_Tags) {
                fetchTags = true;
                break;
            }
        }
    }
    const allTestCases: ITestCaseItem[] = (yield effects.select(getTestCaseItemsFromState, fetchTags)) as any;
    if (filterActiveOrValueSet) {
        testHubRefreshLocalContextData.setIsAuthorFilterActiveOrSet(true);
        const filteredTestCaseIds: number[] = (yield effects.call(filterTestCases, allTestCases)) as any;
        const announcement =
            filteredTestCaseIds && filteredTestCaseIds.length > 0 ? format(TestCaseCountText, filteredTestCaseIds.length) : ZeroTestCaseText;
        announce(announcement);
        yield effects.put(AuthorActions.updateFilteredTestCases(filteredTestCaseIds));
    }
    else {
        testHubRefreshLocalContextData.setIsAuthorFilterActiveOrSet(false);
        const announcement = allTestCases && allTestCases.length > 0 ? format(TestCaseCountText, allTestCases.length) : ZeroTestCaseText;
        announce(announcement);
        yield effects.put(AuthorActions.updateFilteredTestCases(undefined));
    }
}

/**
 * Runs to fetch test case counts for suite tree
 */
export function* fetchSuiteTestCaseCounts(): SagaIterator {
    const planId = (yield effects.select(getPlanId)) as any;
    const isFilterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (isFilterActiveOrValueSet) {
        yield effects.call(fetchFilteredSuiteTestCaseCounts, planId);
    }
    else {
        yield effects.put(SuitesActions.getSuiteTestCasesCount(planId, [], []));
    }
}

/**
 * Runs whenever the filter state is toggled (enabled/disabled)
 */
export function* filterToggled(action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>): SagaIterator {

    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any
    const telemetryPayload = {
        isFilterVisible: yield effects.select(isFilterVisible),
        isFilterActive: filterActiveOrValueSet,
        pivot: TestHubRefreshConstants.DEFINE_TAB_ID,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.FILTER_TOGGLED, telemetryPayload));
}

/**
 * Returns true if either the filter is active or if any of the filter value is set
 * Filter active = filter is visible AND any of the filter value is set
 * We need this method because we want to evaluate filter even if it is hidden but a value in it is set.
 * In cases where the State filter is set in Execute tab, filter bar is hidden and then you navigate to Define tab; we still need to evaluate the filter even though it's hidden.
 */
export function* isFilterActiveOrFilterValueSet(): SagaIterator {
    const filterActive: boolean = (yield effects.select(isFilterActive)) as any;

    const filterState: IFilterState | undefined = (yield effects.select(getCurrentFilterState)) as any;
    const isFilterValueSet: boolean = (yield effects.call(isAnyAuthorFilterValueSet, filterState)) as any;

    return filterActive || isFilterValueSet;
}

/**
 * Runs to load initial test cases present in json island
 */
export function* loadInitialTestCases(action: ActionsOfType<AuthorActions, AuthorActionTypes.loadInitialTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const data: TestPlansHubRefreshData = (yield effects.call(getDataProviderContent, pageContext)) as any;

    yield effects.put(AuthorActions.loadInitialTestCasesSucceeded(data.testCases));
    if (data.testCases) {
        yield effects.put(SuitesActions.updateLoadingStatus(false));
    }
    
    yield effects.put(TestHubRefreshActions.updateDefineTabCustomColumnFieldMap(data.defineTabCustomColumnFieldMap));
    let existingColumnOptionFields = (yield effects.select(getDefineColumnOptionFields)) as any;

    if (!existingColumnOptionFields) {
        yield effects.put(TestHubRefreshActions.loadDefineColumnOptionFieldsSucceeded(data.defineColumnOptionFields));
    }
    yield effects.put(AuthorActions.fetchColumnWidths());
}

/**
 * Runs when user clicks on a suite in the suite tree
 */
export function* changeSelectedSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>): SagaIterator {
    // clear the filtered items in state, since the item ids present in it will not be valid for the new suite
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    let registryEntries = {
        SelectedSuiteId: action.payload.selectedSuiteId.toString()
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);

    yield effects.put(AuthorActions.updateFilteredTestCases(undefined));
    yield effects.put(AuthorActions.unloadPreviousTestCases());
    yield effects.put(AuthorActions.loadTestCasesForSelectedSuite(action.payload.planId, action.payload.selectedSuiteId));
}

export function* editColumnOptionFieldsClicked(
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.editColumnOptionFieldsClicked>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const columnOptionFields = (yield effects.select(getDefineColumnOptionFields)) as any;

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const allColumnFields = getAllPossibleFields();

    const updateColumnFieldsChannel: Channel<{}> = (yield effects.call(channel)) as any;

    const editColumnOptionFieldsProps = {
        onSuccess: (displayColumns: string[], displayColumnNames: string[], newColumnsAdded: boolean) => {
            updateColumnField(updateColumnFieldsChannel, displayColumns, displayColumnNames, newColumnsAdded);
        },
        existingColumnFields: columnOptionFields,
        allColumnFields: allColumnFields.map((field) => ({
            referenceName: field.key,
            displayName: field.name
        }))
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/ColumnOptionsPanelProxy"],
            wrappedType: "column-options-panel-proxy",
            wrappedProps: editColumnOptionFieldsProps
        };
        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(updateColumnFieldsChannel)) as any;
    const oldColumnOptionFields = (yield effects.select(getDefineColumnOptionFields)) as any;
    const newColumnOptionFields = payload.displayColumns;

    let customColumnFieldMap: { [columnId: string]: string} = {};
    payload.displayColumns.forEach((key: string, value: string) => {
        if (isCustomField(key)) {
            customColumnFieldMap[key] = payload.displayColumnNames[value]
        }
    });

    yield effects.put(TestHubRefreshActions.updateDefineTabCustomColumnFieldMap(customColumnFieldMap));
    yield effects.put(TestHubRefreshActions.loadDefineColumnOptionFieldsSucceeded(payload.displayColumns));

    if (payload.newColumnsAdded) {
        yield effects.put(AuthorActions.loadTestCasesForSelectedSuite(action.payload.planId, action.payload.suiteId));
    }

    const registryEntries = {
        NewDefineColumnOptions: payload.displayColumns && payload.displayColumns.length > 0 ? payload.displayColumns.join(",") : ""
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        oldColumnOptionFields: oldColumnOptionFields,
        newColumnOptionFields: newColumnOptionFields,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
        pivot: TestHubRefreshConstants.DEFINE_TAB_ID
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.COLUMN_OPTIONS_EDIT, telemetryPayload));
}

/**
 * Runs when user selects a suite in the suite tree and loads the test cases for that suite
 */
export function* loadTestCasesForSelectedSuite(action: ActionsOfType<AuthorActions, AuthorActionTypes.loadTestCasesForSelectedSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const data: TestPlansHubRefreshData = (yield effects.call(getDataProviderContent, pageContext)) as any;

    // Reload is set to true when continuation token is not present. Since the new set will also contain the already loaded test cases, reload will ensure correct behavior
    // This can be removed once we start passing the continuation token.
    let reload = false;
    let continuationToken = action.payload.continuationToken;
    let testCasesCount;
    if (!continuationToken) {
        reload = true;
        testCasesCount = (yield effects.select(getTestCasesCount)) as any;
        continuationToken = format("0;{0}", TestHubRefreshConstants.INITIAL_PAGE_SIZE);
    }

    // if continuation token is not present and also no test case are loaded from the data provider, then spinner should show up.
    if (reload && !testCasesCount) {
        yield effects.put(SuitesActions.updateLoadingStatus(true));
    }

    const existingColumnOptionFields = (yield effects.select(getDefineColumnOptionFields)) as any;

    if (!existingColumnOptionFields) {
        yield effects.put(TestHubRefreshActions.updateDefineTabCustomColumnFieldMap(data.defineTabCustomColumnFieldMap));
        yield effects.put(TestHubRefreshActions.loadDefineColumnOptionFieldsSucceeded(data.defineColumnOptionFields));
    }
    // show paging indication
    yield effects.put(TestHubRefreshActions.updatePagingStatus(true));

    const columnOptionFields = (yield effects.select(getWITColumnOptionFields)) as any;
    const testCasesWithToken: TestArtifactWithContinuationToken<TestCase> = (yield effects.call(
        [dataService, dataService.getTestCasesForSuite],
        action.payload.testPlanId,
        action.payload.testSuiteId,
        undefined,
        undefined,
        columnOptionFields,
        continuationToken,
        true,
        columnOptionFields ? ExcludeFlags.PointAssignments : ExcludeFlags.PointAssignments | ExcludeFlags.ExtraInformation,
        columnOptionFields ? true : false
    )) as any;

    yield effects.put(AuthorActions.loadTestCasesForSelectedSuiteSucceeded(testCasesWithToken.results, reload));
    // we got a new payload of results here, trigger a re-evaluation of the filter in case new items match the filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(AuthorActions.filterTestCasesInState());
    }

    if (reload) {
        yield effects.put(SuitesActions.updateLoadingStatus(false));
    }
    const pageSize: number = (yield effects.select(getTestCasePageSize)) as any;
    if (testCasesWithToken.continuationToken) {
        const tokenSplits: string[] = testCasesWithToken.continuationToken.split(";");
        if (tokenSplits && tokenSplits.length == 2) {
            tokenSplits[1] = pageSize.toString();
            testCasesWithToken.continuationToken = tokenSplits.join(";");
        }
        yield effects.put(
            AuthorActions.loadTestCasesForSelectedSuite(action.payload.testPlanId, action.payload.testSuiteId, testCasesWithToken.continuationToken)
        );
    }

    // hide paging indication
    yield effects.put(TestHubRefreshActions.updatePagingStatus(false));
    if (!testCasesWithToken.continuationToken) {
        const telemetryPayload = {
            planId: action.payload.testPlanId,
            suiteId: action.payload.testSuiteId,
            testCasesCount: yield effects.select(getTestCasesCount),
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
            testCasePageSize: pageSize
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.GET_TEST_CASES_FOR_SUITE, telemetryPayload));
    }
}

// Function used to enable/disable the busy view spinner.
// This function can be used to update the busy view spinner from a non-generating function.
// It takes the response from a channel which indicates whether the busy view spinner needs to be enabled or disabled
export function* updateBusyViewSpinnerStatus(chan: Channel<boolean>) {
    while (true) {
        const busySpinnerEnable: boolean = yield effects.take(chan);
        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(busySpinnerEnable));
    }
}

export function* addExistingTestCases(action: ActionsOfType<AuthorActions, AuthorActionTypes.addExistingTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const addTestCasesChannel: Channel<ILoadTestCasesPayload> = (yield effects.call(channel)) as any;
    const busySpinnerChannel: Channel<boolean> = (yield effects.call(channel)) as any;
    const currentTestCases: TestCase[] = (yield effects.select(getTestCasesFromState)) as any;
    
    const addToExistingTestCasesProps = {
        areaPath: action.payload.areaPath,
        okCallback: (testCaseIds: number[]) =>
            onAddCases(pageContext, addTestCasesChannel, action.payload.planId, action.payload.suiteId, testCaseIds, currentTestCases, busySpinnerChannel)
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/AddToExistingTestCasesProxy"],
            wrappedType: "add-to-existing-dialog-proxy",
            wrappedProps: addToExistingTestCasesProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };
        return React.createElement(LegacyComponent, properties);
    });
    // Enable the busy spinner component on "Add test cases" button click. Simultaneously listen on the addTestCasesChannel
    const [updateBusyViewSpinnerTask, payload] = (yield effects.all([effects.fork(updateBusyViewSpinnerStatus, busySpinnerChannel), effects.take(addTestCasesChannel)])) as any;
    yield effects.put(AuthorActions.loadTestCasesForSelectedSuite(payload.planId, payload.suiteId));
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    yield effects.put(AuthorActions.fetchSuiteTestCasesCount());

    // if the test cases are being added to initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === payload.suiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }

    const telemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        testCaseIds: payload.testCaseIds,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.ADD_EXISTING_TEST_CASES, telemetryPayload));
}

export function* showSuiteLevelGrid(action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.showSuiteLevelGrid>): SagaIterator {
    let testcaseIds: number[] = [];

    const columnAndSortOrder: IColumnAndSortOrder = (yield effects.select(getColumnAndSortOrder)) as any;
    const allTestCasesMap = (yield effects.select(getTestCasesMapFromState)) as any;
    const columnOptionFields = (yield effects.select(getDefineColumnOptionFields)) as any;
    let testCaseItems: ITestCaseItem[] = (yield effects.select(getTestCaseItemsFromState)) as any;
    if (allTestCasesMap && columnOptionFields) {
        for (const testCase of testCaseItems) {
            testCase.columnOptionValues = getAllColumnValues(columnOptionFields, allTestCasesMap[testCase.id]);
        }
    }

    if (testCaseItems) {
        // in case any sorting has been applied, take the test case ids in the particular sorted order
        if (columnAndSortOrder && columnAndSortOrder.sortOrder !== undefined) {
            testCaseItems = testCaseItems.sort(getTestCaseItemsComparator(columnAndSortOrder.columnId, columnAndSortOrder.sortOrder));
        }
        // in cases where test cases have been re-ordered, sort the test case ids by the new order else its sorts by existing order
        else {
            testCaseItems = testCaseItems.sort(getTestCaseItemsComparator(AuthorConstants.OrderColumn, SortOrder.ascending));
        }
        for (let i = 0; i < testCaseItems.length; i++) {
            testcaseIds[i] = testCaseItems[i].id;
        }
    }

    yield effects.put(
        AuthorActions.addOrEditTestCasesUsingGrid(
            testcaseIds,
            action.payload.planId,
            action.payload.suiteId,
            action.payload.planName,
            action.payload.areaPath,
            action.payload.iterationPath
        )
    );
}

export function* addOrEditTestCasesUsingGrid(action: ActionsOfType<AuthorActions, AuthorActionTypes.addOrEditTestCasesUsingGrid>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    yield effects.put(TestHubRefreshActions.setGridView(true));
    const selectedSuite = (yield effects.select(getTestSuiteById, action.payload.suiteId)) as any;
    let columnOptionFields: string[] = (yield effects.select(getWITColumnOptionFieldsArray)) as any;
    const isGridDirtyChannel: Channel<{}> = (yield effects.call(channel)) as any;

    // remove the duplicate id column from list of columns
    columnOptionFields = columnOptionFields.filter((value: string) => {
        return value !== AuthorConstants.WorkItemId;
    });
    const gridViewProxyComponentProps = {
        gridOption: GridOptions.EditGrid,
        selectedSuite: selectedSuite,
        plan: {
            id: action.payload.planId,
            name: action.payload.planName,
            iteration: action.payload.planIteration,
            areaPath: action.payload.planAreaPath,
            buildUri: ""
        },
        testCaseIds: action.payload.testCaseIds,
        columnOptions: columnOptionFields,
        isDirtyCallback: (isDirty: boolean) => {
            setIsGridDirty(isGridDirtyChannel, isDirty);
        }
    };

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/GridViewProxyComponent"],
            wrappedType: "grid-view-proxy-component",
            wrappedProps: gridViewProxyComponentProps
        };
        return React.createElement(LegacyComponent, properties);
    });

    const telemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        gridViewSelected: true,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.GRID_VIEW_SELECTED, telemetryPayload));

    // Here we continuously listen to any updates from the grid indicating change in dirty status.
    // The while loop is in place because without it we would get only the first change and not successive ones.
    while (true) {
        const payload = (yield effects.take(isGridDirtyChannel)) as any;
        yield effects.put(TestHubRefreshActions.setIsGridDirty(payload.isDirty));
    }
}

export function* closeGrid(action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.closeGrid>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    yield effects.put(TestHubRefreshActions.setGridView(false));

    const gridViewProxyComponentProps = {
        gridOption: GridOptions.CloseGrid
    };

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/GridViewProxyComponent"],
            wrappedType: "grid-view-proxy-component",
            wrappedProps: gridViewProxyComponentProps
        };
        return React.createElement(LegacyComponent, properties);
    });

    yield effects.put(SuitesActions.getSuiteTestCasesCount(action.payload.planId, [], []));
}

/**
 * Runs when user selects assign configurations to test case in the author pivot context menu.
 */
export function* assignConfigurationToTestCases(action: ActionsOfType<AuthorActions, AuthorActionTypes.assignConfigurationToTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const showToastNotificationsChannel: Channel<{}> = (yield effects.call(channel)) as any;
    const assignConfigToTestCaseProps = {
        suite: {
            id: action.payload.suiteId
        },
        planId: action.payload.planId,
        dialogType: AuthorConstants.AssignConfigToTestCasesDialog,
        testCaseIds: action.payload.testCaseIds,
        onSuccess: () => {
            showToastNotification(showToastNotificationsChannel);
        }
    };
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/AssignConfigDialogProxy"],
            wrappedType: "assign-config-dialog-proxy",
            wrappedProps: assignConfigToTestCaseProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };

        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(showToastNotificationsChannel)) as any;
    yield effects.put(SuitesActions.showToastNotifications(Resources.ConfigurationUpdated, payload.duration));

    const telemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        testCaseIds: action.payload.testCaseIds,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.ASSIGN_CONFIGURATION_TO_TEST_CASE, telemetryPayload));
}

export function* openNewTestCaseWitForm(action: ActionsOfType<AuthorActions, AuthorActionTypes.openNewTestCaseWitForm>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );

    const loadTestCasesChannel: Channel<ILoadTestCasesPayload> = (yield effects.call(channel)) as any;
    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItemTypeCategory: WorkItemTypeCategory = (yield effects.call(
        [witClient, witClient.getWorkItemTypeCategory],
        tfsPageData.project.id,
        AuthorConstants.WorkItemTypeCategoryTestCase
    )) as any;
    const testCaseWorkItemTypeName = workItemTypeCategory ? workItemTypeCategory.defaultWorkItemType.name : TestCaseWorkItemType
    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    if (workItemService) {
        yield effects.call(
            [workItemService, workItemService.createWorkItem],
            testCaseWorkItemTypeName,
            AuthorConstants.TestCaseCreationMode,
            {
                save: (workItem: any): void => {
                    onAddCases(pageContext, loadTestCasesChannel, action.payload.testplanId, action.payload.testSuiteId, [workItem.id]);
                }
            },
            {
                "System.AreaPath": action.payload.areaPath,
                "System.IterationPath": action.payload.iterationPath
            }
        );
    } else {
        throw new Error("Unable to fetch workitem service instance.");
    }

    const payload: ILoadTestCasesPayload = (yield effects.take(loadTestCasesChannel)) as any;
    yield effects.put(AuthorActions.loadTestCasesForSelectedSuite(payload.planId, payload.suiteId));
    yield effects.put(AuthorActions.fetchSuiteTestCasesCount());

    // if the test case is being added to initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === payload.suiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }

    const telemetryPayload = {
        suiteId: action.payload.testSuiteId,
        planId: action.payload.testplanId,
        testCaseIds: payload.testCaseIds,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.ADD_TEST_CASE_USING_FORM, telemetryPayload));
}

function onAddCases(
    pageContext: IVssPageContext,
    addTestCasesChannel: Channel<ILoadTestCasesPayload>,
    testplanId: number,
    testSuiteId: number,
    testCaseIds: number[],
    currentTestCases?: TestCase[],
    busySpinnerChannel?: Channel<boolean>
) {
    const dataService: ITestHubDataService = pageContext.getService<ITestHubDataService>("ITestHubDataService");
    if (dataService) {
        const params: SuiteTestCaseCreateUpdateParameters[] = [];
        const testCaseIdslength = testCaseIds.length;
        // Populate the busy spinner channel to enable the busy spinner component on button click
        if (busySpinnerChannel) {
            busySpinnerChannel.put(true);
        }
        for (let i = 0; i < testCaseIdslength; i++) {
            if (currentTestCases && currentTestCases.length > 0) {
                const testCase: TestCase[] = currentTestCases.filter((testCase) => testCase.workItem.id === testCaseIds[i]);
                if (testCase && testCase.length > 0) {
                    continue;
                }
            }
            const config: Configuration[] = [];
            const id: WorkItem = { id: testCaseIds[i] };
            const updateParam: SuiteTestCaseCreateUpdateParameters = {
                pointAssignments: config,
                workItem: id
            };
            params.push(updateParam);
        }
        if (params.length > 0) {
            dataService.addTestCasesToSelectedSuite(params, testplanId, testSuiteId)
                .then(() => {
                    addTestCasesChannel.put({ planId: testplanId, suiteId: testSuiteId, testCaseIds: testCaseIds });
                })
                .catch((e) => {
                    if (busySpinnerChannel) {
                        // Disable the busy spinner if no new test cases to be added
                        busySpinnerChannel.put(false);
                    }
                    showErrorToast(pageContext, e.message, e.message);
                })
        }
        else if (busySpinnerChannel) {
            // Disable the busy spinner if no new test cases to be added
            busySpinnerChannel.put(false);
        }
    } else {
        throw new Error("Unable to fetch hub data service instance.");
    }
}

function showToastNotification(showToastNotificationChannel: Channel<any>) {
    showToastNotificationChannel.put({ duration: TestHubRefreshConstants.TOAST_MESSAGE_DURATION });
}

function setIsGridDirty(isGridDirtyChannel: Channel<any>, isDirty: boolean) {
    isGridDirtyChannel.put({ isDirty: isDirty });
}

export function* openExistingTestCaseWitForm(action: ActionsOfType<AuthorActions, AuthorActionTypes.openExistingTestCaseWitForm>): SagaIterator {
    const updateTestCaseChannel: Channel<IUpdateTestCasePayload> = (yield effects.call(channel)) as any;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );

    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    const columnOptionFields = (yield effects.select(getWITColumnOptionFieldsArray)) as any;
    if (workItemService) {
        yield effects.call([workItemService, workItemService.openWorkItem], action.payload.testCaseId, {
            save: (workItem: any): void => {
                const newTestCaseTitle = workItem.getTitle();
                const newTestCaseState = workItem.getState();

                // this returns the assigned-to field in unique name format like 'Azure BugBash <azbash@microsoft.com>'
                let newTestCaseAssignedTo = workItem.getFieldValue(TestHubRefreshConstants.WorkItemField_AssignedTo);
                newTestCaseAssignedTo = getIdentityDisplayString(newTestCaseAssignedTo);

                const columnOptions: { [id: string]: any } = {};
                for (const columnOptionsField of columnOptionFields) {
                    columnOptions[columnOptionsField] = workItem.getFieldValue(columnOptionsField);
                }

                updateTestCaseChannel.put({
                    testCaseId: action.payload.testCaseId,
                    testCaseTitle: newTestCaseTitle,
                    testCaseState: newTestCaseState,
                    testCaseAssignedTo: newTestCaseAssignedTo,
                    columnOptions: columnOptions
                });
            }
        });
    } else {
        throw new Error("Unable to fetch workitem service instance.");
    }

    // update the test case properties in store on client side only (note that if test case assignedto is changed, we do not get the VSID of the new assignedTo); so we need to fetch new object from server
    const payload: IUpdateTestCasePayload = (yield effects.take(updateTestCaseChannel)) as any;
    yield effects.put(
        AuthorActions.updateTestCase(
            payload.testCaseId,
            payload.testCaseTitle,
            payload.testCaseState,
            payload.testCaseAssignedTo,
            payload.columnOptions
        )
    );

    // if the test cases are being added to initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === action.payload.suiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }

    // fetch the updated test case object from server and update in state (this will fetch the VSID of the assignedto)
    yield effects.put(AuthorActions.getTestCasesFromServer(action.payload.planId, action.payload.suiteId, [action.payload.testCaseId]));

    // A test case was updated, trigger evaluate filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(AuthorActions.filterTestCasesInState());
        yield effects.put(AuthorActions.fetchSuiteTestCasesCount());
    }

    const editTestCaseTelemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        testCaseId: payload.testCaseId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EDIT_TEST_CASE_USING_FORM, editTestCaseTelemetryPayload));
}

export function* getTestCasesFromServer(action: ActionsOfType<AuthorActions, AuthorActionTypes.getTestCasesFromServer>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const returnIdentityRef = true;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const columnOptionFields = (yield effects.select(getWITColumnOptionFields)) as any;

    const testCasesWithToken: TestArtifactWithContinuationToken<TestCase> = (yield effects.call(
        [dataService, dataService.getTestCasesForSuite],
        action.payload.planId,
        action.payload.suiteId,
        action.payload.testCaseIds.join(","),
        undefined,
        columnOptionFields,
        undefined,
        returnIdentityRef,
        columnOptionFields ? ExcludeFlags.PointAssignments : ExcludeFlags.PointAssignments | ExcludeFlags.ExtraInformation,
        columnOptionFields ? true : false
    )) as any;

    if (testCasesWithToken.results) {
        yield effects.put(AuthorActions.getTestCasesFromServerSucceeded(testCasesWithToken.results));
    }
}

export function* removeTestCasesFromSuite(action: ActionsOfType<AuthorActions, AuthorActionTypes.removeTestCasesFromSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    // show spinner while test case(s) are being removed
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));

    yield effects.call(
        [dataService, dataService.removeTestCasesFromSuite],
        action.payload.planId,
        action.payload.suiteId,
        action.payload.testCaseIds.join(",")
    );

    // clear selection after remove
    if (action.payload.selection) {
        action.payload.selection.clear();
    }
    yield effects.put(AuthorActions.removeTestCasesFromSuiteSucceeded(action.payload.planId, action.payload.suiteId, action.payload.testCaseIds));
    // Test Case(s) removed , remove the spinner
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    yield effects.put(AuthorActions.fetchSuiteTestCasesCount());

    // if the test cases are being removed from initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === action.payload.suiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }

    const removeTestCaseFromSuiteTelemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        testCaseIds: action.payload.testCaseIds,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.REMOVE_TEST_CASES, removeTestCaseFromSuiteTelemetryPayload));
}

function updateColumnField(updateColumnFieldsChannel: Channel<any>, displayColumns: string[], displayColumnNames: string[], newColumnsAdded: boolean) {
    updateColumnFieldsChannel.put({ displayColumns: displayColumns, displayColumnNames: displayColumnNames, newColumnsAdded: newColumnsAdded });
}

export function* reorderTestCases(action: ActionsOfType<AuthorActions, AuthorActionTypes.reorderTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    // sequence number is 1 less than the order shown in test case table
    let sequenceNumber = action.payload.startTestCaseOrder - 1;
    const params = [] as SuiteEntryUpdateParams[];

    for (let i = 0; i < action.payload.shiftedTestCaseIds.length; i++) {
        //All the shifted test case ids are added to the payload for reorderSuiteEntries API
        if (action.payload.shiftedTestCaseIds[i] > 0) {
            params.push({
                id: action.payload.shiftedTestCaseIds[i],
                sequenceNumber: sequenceNumber,
                suiteEntryType: SuiteEntryTypes.TestCase
            });
            sequenceNumber++;
        }
    }
    const suiteEntries = (yield effects.call([dataService, dataService.reorderSuiteEntries], params, action.payload.suiteId)) as any;
    if (suiteEntries) {
        yield effects.put(AuthorActions.reorderTestCasesSucceeded(suiteEntries));
    }
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        testCaseIds: action.payload.draggedtestCaseIds,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.REORDER_TEST_CASES, telemetryPayload));
}

export function* bulkEditTestCases(action: ActionsOfType<AuthorActions, AuthorActionTypes.bulkEditTestCases>): SagaIterator {
    const bulkUpdateTestCasesChannel: Channel<any> = (yield effects.call(channel)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItemTypeCategory: WorkItemTypeCategory = (yield effects.call(
        [witClient, witClient.getWorkItemTypeCategory],
        pageData.project.id,
        AuthorConstants.WorkItemTypeCategoryTestCase
    )) as any;
    const testCaseWorkItemTypeName = workItemTypeCategory ? workItemTypeCategory.defaultWorkItemType.name : TestCaseWorkItemType

    let projectTypeMapping = new Array();
    projectTypeMapping[pageData.project.name] = [testCaseWorkItemTypeName];
    const bulkEditTestCasesProps = {
        testCaseIds: action.payload.testCaseIds,
        projectTypeMapping: projectTypeMapping,
        okCallback: (dialogResult: any) => onEditTestCases(dialogResult, bulkUpdateTestCasesChannel)
    };
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/TestCaseBulkEditProxy"],
            wrappedType: "testcase-bulk-edit-proxy",
            wrappedProps: bulkEditTestCasesProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };

        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(bulkUpdateTestCasesChannel)) as any;
    if (payload) {
        if (action.payload.testCaseIds.length > 250) {
            // getTestCaseList API call fails when query string exceeds 2048 characters. Hence loading the entire suite. (most likely user would have done a bulk edit on all test cases in the suite)
            yield effects.put(AuthorActions.loadTestCasesForSelectedSuite(action.payload.planId, action.payload.suiteId));
        } else {
            // fetch the updated test cases from server
            yield effects.put(AuthorActions.getTestCasesFromServer(action.payload.planId, action.payload.suiteId, action.payload.testCaseIds));
        }
        const telemetryPayload = {
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            testCaseIds: action.payload.testCaseIds,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.BULK_EDIT_TEST_CASES, telemetryPayload));
    }
}

function onEditTestCases(dialogResult: any, bulkUpdateTestCasesChannel: Channel<any>) {
    if (dialogResult) {
        bulkUpdateTestCasesChannel.put({ dialogResult: dialogResult });
    }
}

export function* saveColumnWidths(action: ActionsOfType<AuthorActions, AuthorActionTypes.saveColumnWidths>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;

    if (action.payload && action.payload.columnAndWidthMap) {
        let registryEntries;
        registryEntries = {
            DefineColumnWidths: action.payload.columnAndWidthMap
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    }
}

export function* fetchColumnWidths(action: ActionsOfType<AuthorActions, AuthorActionTypes.fetchColumnWidths>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;

    // fetch the column widths from db
    let result: { [key: string]: any } = (yield effects.call(
        [settingsService, settingsService.getEntriesAsync],
        "",
        SettingsUserScope.Me,
        "project",
        tfsPageData.project.id
    )) as any;
    if (result && result["DefineColumnWidths"]) {
        yield effects.put(TestHubRefreshActions.changeDefineColumnWidths(undefined, undefined, result["DefineColumnWidths"]));
    }
}

export function* exportTestCasesToCSV(action: ActionsOfType<SuitesActions, SuitesActionsTypes.exportTestCasesToCSV>): SagaIterator {
    let testCaseIds: number[] = [];
    if (action.payload.testCaseIds) {
        testCaseIds = action.payload.testCaseIds;
    } else {
        const testCases: ITestCaseItem[] = (yield effects.select(getTestCaseItemsFromState)) as any;
        if (testCases && testCases.length > 0) {
            testCaseIds = testCases.map((testcase) => testcase.id) as any;
        }
    }

    if (testCaseIds.length > 0) {
        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
        const columnOptions = (yield effects.select(getDefineColumnOptionFields)) as any;
        // include only WIT fields and custom fields
        let filteredColumnOptions = columnOptions.filter((field: string) => {
            return !OtherColumnOptions[field];
        });
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const fileName: string = action.payload.planName + "_" + action.payload.suiteName + ".csv";
        const exportProxyComponentProps = {
            testCaseIds: testCaseIds,
            columnOptions: filteredColumnOptions,
            okCallBack: (testCasesData: any) => {
                let exporter = new TestCaseCSVExporterUtility(fileName);
                exporter.formatTestCaseData(testCasesData, testCaseIds);
            }
        };

        const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
        yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
            const properties: ILegacyComponentProperties = {
                modules: ["TestManagement/Scripts/Shared/TestCaseExportProxyComponent"],
                wrappedType: "testcases-export-proxy-component",
                wrappedProps: exportProxyComponentProps
            };
            return React.createElement(LegacyComponent, properties);
        });
        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
        const telemetryPayload = {
            testCaseIdsCount: testCaseIds.length,
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            columnOptions: columnOptions,
            fileType: "csv",
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EXPORT_TESTCASES, telemetryPayload));
    }
}

export function* exportTestCasesToExcel(action: ActionsOfType<SuitesActions, SuitesActionsTypes.exportTestCasesToExcel>): SagaIterator {
    let testCaseIds: number[] = [];
    if (action.payload.testCaseIds) {
        testCaseIds = action.payload.testCaseIds;
    } else {
        const testCases: ITestCaseItem[] = (yield effects.select(getTestCaseItemsFromState)) as any;
        if (testCases && testCases.length > 0) {
            testCaseIds = testCases.map((testcase) => testcase.id) as any;
        }
    }

    if (testCaseIds.length > 0) {
        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
        const columnOptions = (yield effects.select(getDefineColumnOptionFields)) as any;
        // include only WIT fields and custom fields
        let filteredColumnOptions = columnOptions.filter((field: string) => {
            return !OtherColumnOptions[field];
        });
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const fileName: string = action.payload.planName + "_" + action.payload.suiteName + ".xlsx";

        // Export test cases to an excel file
        const dataService: ITestHubDataService = pageContext.getService<ITestHubDataService>("ITestHubDataService");
        if (dataService) {
            let exportTestCaseParams: ExportTestCaseParams = {
                testCaseIds: testCaseIds,
                testPlanId: action.payload.planId,
                testSuiteId: action.payload.suiteId,
            }

            dataService.exportTestCasesToExcel(exportTestCaseParams)
                .then((data) => {
                    if (data) {
                        const blob = new Blob([data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,"});
                        if (checkIfIsIE()) {
                            (window.navigator as any).msSaveBlob(blob, fileName); // IE11 support
                        } else {
                            const a = window.document.createElement("a");
                            const blobUrl = window.URL.createObjectURL(blob);
                            a.href = blobUrl;
                            a.download = fileName;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(blobUrl); // Dispose blob
                        }
                    }
                })
                .catch((e) => {
                    const telemetryPayload = {
                        testCaseIdsCount: testCaseIds.length,
                        planId: action.payload.planId,
                        suiteId: action.payload.suiteId,
                        columnOptions: columnOptions,
                        fileType: "xlsx",
                    };

                    TelemetryActions.publishTelemetry(TelemetryConstants.EXPORT_TESTCASES_FAILURE, telemetryPayload);
                    showErrorToast(pageContext, Resources.ExportTestCasesToXLSXError, Resources.ExportTestCasesToXLSXError);
                })
        }

        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
        const telemetryPayload = {
            testCaseIdsCount: testCaseIds.length,
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            columnOptions: columnOptions,
            fileType: "xlsx",
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EXPORT_TESTCASES, telemetryPayload));
    }
}

export function* getTestCasesCloneOpState(action: ActionsOfType<AuthorActions, AuthorActionTypes.getTestCasesCloneOpState>): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        // We need to show a toast message on the initial call.
        if (action.payload.initialCall) {
            yield effects.put(
                SuitesActions.showToastNotifications(Resources.TestCasesCloneInitiated, TestHubRefreshConstants.TOAST_MESSAGE_DURATION)
            );
        }

        let cloneTestSuiteOperationInformation: CloneTestCaseOperationInformation = (yield effects.call(
            [dataService, dataService.getTestCaseCloneInformation],
            action.payload.opId,
            action.payload.project
        )) as any;
        let state: CloneOperationState = CloneOperationState.Queued;
        if (cloneTestSuiteOperationInformation && cloneTestSuiteOperationInformation.cloneOperationResponse) {
            state = cloneTestSuiteOperationInformation.cloneOperationResponse.state;
        }

        if (state !== CloneOperationState.Succeeded) {
            yield effects.delay(5000);
            yield effects.put(
                AuthorActions.getTestCasesCloneOpState(
                    action.payload.sourcePlanId,
                    action.payload.sourceSuiteId,
                    action.payload.destinationPlanId,
                    action.payload.destinationSuiteId,
                    action.payload.destinationProject,
                    action.payload.opId,
                    false,
                    action.payload.project
                )
            );
        } else {
            const goToDestinationSuiteChannel: Channel<{}> = (yield effects.call(channel)) as any;
            // Show a toast message indicating that the clone operation has completed.
            if (action.payload.sourcePlanId === action.payload.destinationPlanId) {
                yield effects.put(
                    SuitesActions.showToastNotifications(
                        Resources.TestCasesCloneCompleted,
                        AuthorConstants.TOAST_MESSAGE_DURATION,
                        Resources.Refresh,
                        () => {
                            goToDestinationSuite(goToDestinationSuiteChannel, action.payload.sourcePlanId, undefined, undefined, true);
                        }
                    )
                );

                while (true) {
                    const payload = (yield effects.take(goToDestinationSuiteChannel)) as any;
                    yield effects.put(SuitesActions.reloadTestPlanAndSuites(payload.sourcePlanId));
                }
            } else {
                yield effects.put(
                    SuitesActions.showToastNotifications(
                        Resources.TestCasesCloneCompleted,
                        AuthorConstants.TOAST_MESSAGE_DURATION,
                        Resources.GoToDestinationSuite,
                        () => {
                            goToDestinationSuite(
                                goToDestinationSuiteChannel,
                                action.payload.destinationPlanId,
                                action.payload.destinationSuiteId,
                                action.payload.destinationProject,
                                false
                            );
                        }
                    )
                );

                while (true) {
                    const payload = (yield effects.take(goToDestinationSuiteChannel)) as any;
                    yield effects.put(
                        AuthorActions.openSuiteInNewWindow(payload.destinationProject, payload.destinationPlanId, payload.destinationSuiteId)
                    );
                }
            }
        }
    } catch (e) {
        yield effects.put(SuitesActions.showToastNotifications(e.message, AuthorConstants.TOAST_MESSAGE_DURATION));
    }
}

export function* openSuiteInNewWindow(action: ActionsOfType<AuthorActions, AuthorActionTypes.openSuiteInNewWindow>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: action.payload.project,
        pivots: TestHubRefreshConstants.DEFINE_TAB_ID
    };
    //constructing new plan url using contribution id
    let url = (yield effects.call([locationService, locationService.routeUrl], AuthorConstants.contributionId, routeValues)) as any;
    url += format(AuthorConstants.queryParams, action.payload.planId, action.payload.suiteId);

    let newWindow = (yield effects.call([window, window.open], url, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }
}

function goToDestinationSuite(goToDestinationSuiteChannel: Channel<any>, planId: number, suiteId?: number, project?: string, refresh?: boolean) {
    if (refresh) {
        goToDestinationSuiteChannel.put({ sourcePlanId: planId });
    } else {
        goToDestinationSuiteChannel.put({ destinationPlanId: planId, destinationSuiteId: suiteId, destinationProject: project });
    }
}

const _initializeAuthorPivot = errorHandledSaga(function* (
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    yield effects.call(initializeAuthorPivot, action);
});

const _filterTestCasesInState = errorHandledSaga(function* (
): SagaIterator {
    yield effects.call(filterTestCasesInState);
});

const _fetchSuiteTestCasesCount = errorHandledSaga(function* (
): SagaIterator {
    yield effects.call(fetchSuiteTestCaseCounts);
});

const _filterToggled = errorHandledSaga(function* (
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    yield effects.call(filterToggled, action);
});

const _loadInitialTestCases = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.loadInitialTestCases>
): SagaIterator {
    yield effects.call(loadInitialTestCases, action);
});

const _changeSelectedSuite = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>
): SagaIterator {
    yield effects.call(changeSelectedSuite, action);
});

const _editColumnOptionFieldsClicked = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.editColumnOptionFieldsClicked>
): SagaIterator {
    yield effects.call(editColumnOptionFieldsClicked, action);
});

const _loadTestCasesForSelectedSuite = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.loadTestCasesForSelectedSuite>
): SagaIterator {
    yield effects.call(loadTestCasesForSelectedSuite, action);
});

const _addExistingTestCases = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.addExistingTestCases>
): SagaIterator {
    yield effects.call(addExistingTestCases, action);
});

const _showSuiteLevelGrid = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.showSuiteLevelGrid>
): SagaIterator {
    yield effects.call(showSuiteLevelGrid, action);
});

const _addOrEditTestCasesUsingGrid = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.addOrEditTestCasesUsingGrid>
): SagaIterator {
    yield effects.call(addOrEditTestCasesUsingGrid, action);
});

const _closeGrid = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.closeGrid>
): SagaIterator {
    yield effects.call(closeGrid, action);
});

const _assignConfigurationToTestCases = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.assignConfigurationToTestCases>
): SagaIterator {
    yield effects.call(assignConfigurationToTestCases, action);
});

const _openNewTestCaseWitForm = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.openNewTestCaseWitForm>
): SagaIterator {
    yield effects.call(openNewTestCaseWitForm, action);
});

const _openExistingTestCaseWitForm = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.openExistingTestCaseWitForm>
): SagaIterator {
    yield effects.call(openExistingTestCaseWitForm, action);
});

const _getTestCasesFromServer = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.getTestCasesFromServer>
): SagaIterator {
    yield effects.call(getTestCasesFromServer, action);
});

const _removeTestCasesFromSuite = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.removeTestCasesFromSuite>
): SagaIterator {
    yield effects.call(removeTestCasesFromSuite, action);
});

const _reorderTestCases = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.reorderTestCases>
): SagaIterator {
    yield effects.call(reorderTestCases, action);
});

const _bulkEditTestCases = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.bulkEditTestCases>
): SagaIterator {
    yield effects.call(bulkEditTestCases, action);
});

const _saveColumnWidths = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.saveColumnWidths>
): SagaIterator {
    yield effects.call(saveColumnWidths, action);
});

const _fetchColumnWidths = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.fetchColumnWidths>
): SagaIterator {
    yield effects.call(fetchColumnWidths, action);
});

const _exportTestCasesToCSV = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.exportTestCasesToCSV>
): SagaIterator {
    yield effects.call(exportTestCasesToCSV, action);
});

const _exportTestCasesToExcel = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.exportTestCasesToExcel>
): SagaIterator {
    yield effects.call(exportTestCasesToExcel, action);
});

const _getTestCasesCloneOpState = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.getTestCasesCloneOpState>
): SagaIterator {
    yield effects.call(getTestCasesCloneOpState, action);
});

const _openSuiteInNewWindow = errorHandledSaga(function* (
    action: ActionsOfType<AuthorActions, AuthorActionTypes.openSuiteInNewWindow>
): SagaIterator {
    yield effects.call(openSuiteInNewWindow, action);
});